import React, { useMemo, useCallback, useState } from 'react';
import { Box, Typography, FileUpload } from '@lucid/lego';
import type { InitialFile } from '@lucid/lego';
import { useFileUpload } from '../../../../../hooks/useFileUpload';
import { useUploadProspectDocument } from '../../../../../api/services/lupp/prospect/prospect';
import { FileHubApi } from '../../../../../api/services/lupp/lupp.service';
import { useStore } from '../../../../../store/store';
import type { ProspectFileMetadata } from '../../../../../api/services/lupp/prospect/dto.prospect';
import styles from './DocumentsTab.module.scss';

/** Bytes per megabyte for size conversion */
const BYTES_PER_MB = 1024 * 1024;

type DocumentsTabProps = {
  theme: 'light' | 'dark';
  prospectId: string;
  prospectFilesMetadata?: ProspectFileMetadata[];
  labels?: Record<string, string>;
};

/**
 * Documents tab component for prospect details
 * Allows file uploads with automatic file tracking per prospect
 * @component
 * @param props - Component props
 * @param props.theme - Theme for the component
 * @param props.prospectId - Prospect identifier
 * @param props.prospectFilesMetadata - Existing files metadata from API
 * @param props.labels - i18n labels
 * @returns JSX.Element
 */
const DocumentsTab: React.FC<DocumentsTabProps> = ({
  theme,
  prospectId,
  prospectFilesMetadata = [],
  labels,
}) => {
  const [successMessage, setSuccessMessage] = useState<string>('');
  const selectedProspect = useStore((state) => state.selectedProspect);
  const addUploadedProspectFile = useStore((state) => state.addUploadedProspectFile);
  const uploadProspectDocument = useUploadProspectDocument();

  // Merge API files with locally uploaded files, avoiding duplicates
  const initialFiles: InitialFile[] = useMemo(() => {
    const apiFiles = prospectFilesMetadata
      .filter((file): file is ProspectFileMetadata & { fileId: string } => !!file.fileId)
      .map(({ fileId, fileNameOriginal, mimeType }) => ({
        fileId,
        fileName: fileNameOriginal,
        mimeType,
      }));

    const apiFileIds = new Set(apiFiles.map((f) => f.fileId));
    const uploadedFiles = selectedProspect?.uploadedFiles || [];

    return [...apiFiles, ...uploadedFiles.filter((f) => !apiFileIds.has(f.fileId))];
  }, [prospectFilesMetadata, selectedProspect?.uploadedFiles]);

  const clearMessages = useCallback(() => {
    setSuccessMessage('');
  }, []);

  const showSuccessMessage = useCallback(
    (message: string) => {
      clearMessages();
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    [clearMessages]
  );

  const { getUploadUrl, handleFileRemove } = useFileUpload({
    customerId: prospectId,
    category: 'generic',
    tags: { prospectId },
    onUploadUrlSuccess: (response) => {
      uploadProspectDocument.mutate({
        moduleName: 'Prospect Management',
        moduleReferenceIdentifier: prospectId,
        fileId: response.fileId,
        tenantId: response.tenantId,
        customerId: response.customerId,
        category: response.category,
        fileNameOriginal: response.fileNameOriginal,
        fileNameSanitized: response.fileNameSanitized,
        mimeType: response.mimeType,
        createdAt: response.createdAt,
      });

      addUploadedProspectFile({
        fileId: response.fileId,
        fileName: response.fileNameOriginal,
        mimeType: response.mimeType,
      });
    },
    onError: () => {
      // Error handling managed by FileUpload component
    },
  });

  const handleValidationError = useCallback(
    (error: { type: 'FILE_TOO_LARGE' | 'INVALID_FILE_TYPE'; file: File; maxFileSize?: number }) => {
      if (error.type === 'FILE_TOO_LARGE') {
        const maxSizeMB = error.maxFileSize ? Math.round(error.maxFileSize / BYTES_PER_MB) : 10;
        const fileSizeMB = (error.file.size / BYTES_PER_MB).toFixed(2);
        return `File size ${fileSizeMB}MB exceeds ${maxSizeMB}MB limit`;
      }

      if (error.type === 'INVALID_FILE_TYPE') {
        const fileExtension = `.${error.file.name.split('.').pop()?.toLowerCase()}`;
        return `File type ${fileExtension} is not supported`;
      }

      return undefined;
    },
    []
  );

  const handleDocumentClick = useCallback(async (fileId: string, fileName: string) => {
    try {
      // Fetch the download URL directly using the axios instance
      const response = await FileHubApi.instance.get<string>(
        `/shared/filehub/files/${fileId}/download`
      );
      const downloadUrl = response.data;

      if (downloadUrl) {
        // Create a temporary anchor element to trigger download in same window
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName; // Suggest filename for download
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  }, []);

  return (
    <Box className={styles.documentsTab}>
      {successMessage && (
        <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.successMessageText}>
          <Typography variant="body" size="medium" color="white" theme={theme}>
            {successMessage}
          </Typography>
        </Box>
      )}

      <Box mb={24}>
        <Typography variant="subtitle" size="small" theme={theme} color="white">
          {labels?.['docs.uploadDocuments'] ?? 'Upload Documents'}
        </Typography>
      </Box>

      <Box mb={24}>
        <FileUpload
          dataTestId="prospect-documents-upload"
          theme={theme}
          getUploadUrl={getUploadUrl}
          onFileRemove={handleFileRemove}
          onValidationError={handleValidationError}
          onDocumentClick={handleDocumentClick}
          onUploadComplete={(_fileUrl, file) => {
            showSuccessMessage(`File "${file.name}" uploaded successfully`);
          }}
          multiple
          accept=".jpg,.png,.gif,.pdf,.doc,.docx,.jpeg"
          label={labels?.['docs.prospectDocuments'] ?? 'Prospect Documents'}
          helperText={
            labels?.['docs.jpgPngGif'] ?? 'JPG, PNG, GIF, PDF, DOC files supported (max 10 MB each)'
          }
          dropzoneText={
            labels?.['docs.dragAndDrop'] ?? 'Drag and drop files here, or click to select'
          }
          initialFiles={initialFiles}
        />
      </Box>
    </Box>
  );
};

export default DocumentsTab;
